import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';

import { useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbsContextType {
  items: Array<BreadcrumbItem>;
  handleBreadcrumbItems: Dispatch<SetStateAction<BreadcrumbItem[]>>;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextType>({} as BreadcrumbsContextType);

const excludedPathnames = [
  /^\/organizations\/[0-9]+$/,
  /^\/competitions\/[0-9]+$/,
  /^\/seasons\/[0-9]+$/,
  /^\/rounds\/[0-9]+$/,
  /^\/fixtures\/[0-9]+$/,
  /^\/competitors\/teams\/[0-9]+$/,
  /^\/competitors\/players\/[0-9]+$/,
  /^\/venues\/[0-9]+$/,
  /^\/localities\/[0-9]+$/,
];

export const BreadcrumbsProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [items, setItems] = useState<Array<BreadcrumbItem>>([]);
  const { pathname } = useLocation();

  useEffect(() => {
    if (excludedPathnames.some(item => item.test(pathname))) {
      return;
    }

    const items = [];
    const pathnames = pathname.split('/').filter(name => name);

    let currentPath = '';

    pathnames.forEach(pathname => {
      currentPath += `/${pathname}`;
      items.push({
        label: pathname,
        path: currentPath,
      });
    });

    setItems(items);
  }, [pathname]);

  return (
    <BreadcrumbsContext.Provider value={{ items, handleBreadcrumbItems: setItems }}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbs = () => useContext(BreadcrumbsContext);
